import * as THREE from 'three';
import { MASK } from '../world/Physics.js';
import { bootstrapBehaviours } from '../game/behaviours/index.js';
import { TEAM, MEANS } from '../game/MatchAuthority.js';

/**
 * First-person character controller.
 *
 * Movement is source-style: acceleration toward a wish-direction with separate
 * ground and air control, so strafing feels responsive without being frictionless.
 * On top of that sit the modern-shooter traversal verbs — sprint, tactical sprint,
 * slide, crouch, mantle — each gated by a small state machine so they can't overlap
 * in ways that break the capsule.
 *
 * Everything a player *feels* is tuned here. The camera rig (bob, sway, recoil) is
 * a separate module that consumes this state.
 */

export const TUNING = {
  eyeHeight: 1.65,
  crouchEyeHeight: 0.95,
  radius: 0.34,
  standHeight: 1.8,
  crouchHeight: 1.15,

  walkSpeed: 4.2,
  sprintSpeed: 6.6,
  tacSprintSpeed: 8.2,
  crouchSpeed: 2.0,
  adsSpeed: 2.6,
  airSpeed: 1.6,

  groundAccel: 62,
  airAccel: 22,
  friction: 9.5,
  stopSpeed: 1.4,

  gravity: 21.5,
  jumpVelocity: 6.4,
  coyoteTime: 0.11,
  jumpBuffer: 0.14,

  slideSpeed: 9.4,
  slideDuration: 0.85,
  slideFriction: 3.4,
  slideMinSpeed: 4.6,
  slideCooldown: 0.55,

  mantleReach: 1.05,
  mantleMaxHeight: 1.55,
  mantleDuration: 0.42,

  stanceLerp: 11,
  maxHealth: 100,
  regenDelay: 4.5,
  regenRate: 26,
};

const STATE = { NORMAL: 0, SLIDING: 1, MANTLING: 2 };

export class Player {
  constructor(ctx, spawn = new THREE.Vector3(0, 1, 0)) {
    this.ctx = ctx;
    const { physics, events } = ctx;
    this.physics = physics;
    this.events = events;

    this.position = spawn.clone(); // feet
    this.velocity = new THREE.Vector3();
    this.yaw = 0;
    this.pitch = 0;

    this.state = STATE.NORMAL;
    this.grounded = false;
    this.wasGrounded = false;
    this.crouching = false;
    this.sprinting = false;
    this.tacSprinting = false;
    this.ads = false;
    this.height = TUNING.standHeight;
    this.eye = TUNING.eyeHeight;

    this.health = TUNING.maxHealth;
    this.lastDamageTime = -99;
    this.dead = false;

    this._coyote = 0;
    this._jumpBuffered = 0;
    this._slideTimer = 0;
    this._slideCooldown = 0;
    this._mantleTimer = 0;
    this._mantleFrom = new THREE.Vector3();
    this._mantleTo = new THREE.Vector3();
    this._sprintHeldTime = 0;
    this._groundMaterial = 'concrete';
    this._footDistance = 0;

    this._wish = new THREE.Vector3();
    this._delta = new THREE.Vector3();
    this._fwd = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._prevState = {};

    // Actor registration lets enemies raycast against the player.
    this.id = 'player';
    this.boundingRadius = 1.2;
    this.matrixWorld = new THREE.Matrix4();
    this.hitboxes = [
      { offset: new THREE.Vector3(0, 1.45, 0), halfExtents: new THREE.Vector3(0.16, 0.19, 0.16), tag: 'head', damageScale: 2.4 },
      { offset: new THREE.Vector3(0, 0.95, 0), halfExtents: new THREE.Vector3(0.3, 0.36, 0.24), tag: 'torso', damageScale: 1 },
      { offset: new THREE.Vector3(0, 0.4, 0), halfExtents: new THREE.Vector3(0.26, 0.42, 0.22), tag: 'legs', damageScale: 0.75 },
    ];
    physics.registerActor(this);
    // The match arbitrates damage and death; this only enrols us as a target with a
    // side. Constructed before the player in `main.js` precisely so this line works.
    ctx.match?.register(this, { team: TEAM.ALPHA, kind: 'player', name: 'YOU' });

    ctx.engine.registerUpdate((dt) => this.update(dt), 'sim');

    /**
     * Attachment behaviours (flinch, overheat, underbarrel, thermal, laser, the
     * firing signature and the smoke field) stand themselves up from here.
     *
     * They belong to nobody's constructor in particular — they are the systems an
     * attachment switches on, not part of the player — but `main.js` is the lead's
     * file and this is the first module in the boot order that owns any of what
     * they touch: the flinch curve is an addition to this player's aim, and the
     * camera rig that integrates it is constructed on the next line of `main`.
     *
     * The call is idempotent and self-guarding, and nothing inside it reads
     * `ctx.player`, `ctx.weapons`, `ctx.ai` or `ctx.fx` at construction time — all
     * four are resolved lazily per frame, the same contract the FX system and the
     * minimap already work under.
     */
    bootstrapBehaviours(ctx);
  }

  get eyePosition() {
    return this._eyeOut ?? (this._eyeOut = new THREE.Vector3()).set(0, 0, 0), this._eyeOut.set(this.position.x, this.position.y + this.eye, this.position.z);
  }

  /** Horizontal speed, used by the camera rig for bob amplitude and FOV kick. */
  get speed() {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  update(dt) {
    if (this.dead) return;
    const input = this.ctx.input;

    this._coyote -= dt;
    this._slideCooldown -= dt;
    this._jumpBuffered -= dt;

    this._look(input, dt);

    if (this.state === STATE.MANTLING) {
      this._updateMantle(dt);
    } else {
      this._updateStance(input, dt);
      this._updateMove(input, dt);
    }

    this._updateHealth(dt);
    this._updateMatrix();
    this._broadcastState();
  }

  /* --------------------------------- look ---------------------------------- */

  _look(input, dt) {
    const scale = this.ads ? input.adsSensScale : 1;
    // dt is required: mouse look is a displacement, but key and stick look are
    // angular rates that have to be integrated over the frame.
    const { yaw, pitch } = input.consumeMouse(scale, dt);
    this.yaw += yaw;
    this.pitch = THREE.MathUtils.clamp(this.pitch + pitch, -Math.PI / 2 + 0.02, Math.PI / 2 - 0.02);
  }

  /* -------------------------------- stance --------------------------------- */

  _updateStance(input, dt) {
    const wantCrouch = input.action('crouch');
    const moving = this.speed > 1.2;
    const forwardHeld = input.action('forward');

    this.ads = input.ads && this.state !== STATE.SLIDING;

    // Sprint requires forward input and is cancelled by ADS. Holding sprint past
    // a threshold escalates to tactical sprint (faster, weapon lowered).
    //
    // `wasSprinting` deliberately ignores crouch. Crouch cancels sprint, but the
    // slide entry below needs to know whether we were sprinting *at the instant
    // crouch was pressed* — testing `this.sprinting` there could never be true,
    // because this same frame's crouch input had already forced it false. That is
    // what made the slide unreachable.
    const sprintInput = input.action('sprint') && forwardHeld && !this.ads && this.grounded;
    const wantSprint = sprintInput && !wantCrouch;
    if (wantSprint && moving) {
      this._sprintHeldTime += dt;
    } else if (!sprintInput) {
      this._sprintHeldTime = 0;
    }
    this.sprinting = wantSprint && moving;
    this.tacSprinting = this.sprinting && this._sprintHeldTime > 0.55;

    // Slide: crouch while sprinting with real speed behind you. Gated on the
    // crouch *press* rather than the held state, so holding crouch after a slide
    // ends doesn't immediately re-enter one.
    if (
      this.state === STATE.NORMAL &&
      input.actionPressed('crouch') &&
      sprintInput &&
      moving &&
      this.speed > TUNING.slideMinSpeed &&
      this._slideCooldown <= 0
    ) {
      this._beginSlide();
    }

    if (this.state === STATE.SLIDING) {
      this._slideTimer -= dt;
      const tooSlow = this.speed < TUNING.slideMinSpeed * 0.55;
      if (this._slideTimer <= 0 || tooSlow || !wantCrouch || !this.grounded) this._endSlide(wantCrouch);
    } else {
      this.crouching = wantCrouch && this.grounded;
    }

    // Never uncrouch into a ceiling.
    const targetHeight = this.crouching || this.state === STATE.SLIDING ? TUNING.crouchHeight : TUNING.standHeight;
    if (targetHeight > this.height && !this._hasHeadroom(targetHeight)) {
      this.crouching = true;
    } else {
      this.height = THREE.MathUtils.damp(this.height, targetHeight, TUNING.stanceLerp, dt);
    }

    const targetEye =
      this.state === STATE.SLIDING
        ? TUNING.crouchEyeHeight * 0.78
        : this.crouching
        ? TUNING.crouchEyeHeight
        : TUNING.eyeHeight;
    this.eye = THREE.MathUtils.damp(this.eye, targetEye, TUNING.stanceLerp, dt);
  }

  _hasHeadroom(h) {
    const probe = this.physics.sweepCapsule(this.position, TUNING.radius, h, _zero);
    return !probe.ceiling;
  }

  _beginSlide() {
    this.state = STATE.SLIDING;
    this._slideTimer = TUNING.slideDuration;
    /**
     * A slide is *not* a crouch, and saying it is quietly hands it every discount
     * crouching earns.
     *
     * The capsule and the eye are already lowered off `state === SLIDING` a few
     * lines below, so nothing about the silhouette depends on this flag. What does
     * depend on it is everything that prices a *braced, still* stance:
     * `Ballistics.coneFor` multiplies hip spread by `crouchScale`,
     * `WeaponSystem._applyRecoil` multiplies recoil by `recoil.crouchScale`, and
     * `Enemy._perceive` scales its awareness ramp by 0.55 for a crouched target.
     * A player travelling at 9.4 m/s has earned none of those.
     *
     * This was unobservable while sliding could not fire at all. It stops being
     * unobservable the moment a `mobility.slideFire` conversion exists: with the
     * crouch discount applied, a slide shot measured a *tighter* cone than a
     * standing hip shot — 28.8 vs 46.2 mrad — which would have made the whole
     * conversion a strict upgrade rather than a trade.
     */
    this.crouching = false;
    // Kick to slide speed along current heading — the burst is what sells it.
    const s = this.speed;
    if (s > 0.01) {
      const boost = Math.max(TUNING.slideSpeed, s * 1.12) / s;
      this.velocity.x *= boost;
      this.velocity.z *= boost;
    }
    this.events.emit('player:slide', { start: true });
    this.events.emit('audio:play', { id: 'slide', volume: 0.8 });
  }

  _endSlide(stillCrouching) {
    this.state = STATE.NORMAL;
    this._slideCooldown = TUNING.slideCooldown;
    this.crouching = stillCrouching;
    this.events.emit('player:slide', { start: false });
  }

  /* -------------------------------- movement -------------------------------- */

  _updateMove(input, dt) {
    const move = input.moveVector(_moveTmp);

    // Basis from yaw only — pitch must not tilt movement.
    this._fwd.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this._right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this._wish.set(0, 0, 0).addScaledVector(this._right, move.x).addScaledVector(this._fwd, -move.z);
    const wishSpeed = this._targetSpeed(move);
    if (this._wish.lengthSq() > 0) this._wish.normalize();

    if (input.actionPressed('jump')) this._jumpBuffered = TUNING.jumpBuffer;

    if (this.state === STATE.SLIDING) {
      // Slides ignore wish-direction almost entirely; you get a little steering only.
      this._applyFriction(dt, TUNING.slideFriction);
      this._accelerate(this._wish, wishSpeed * 0.35, 12, dt);
    } else if (this.grounded) {
      this._applyFriction(dt, TUNING.friction);
      this._accelerate(this._wish, wishSpeed, TUNING.groundAccel, dt);
    } else {
      this._accelerate(this._wish, Math.min(wishSpeed, TUNING.airSpeed * 3), TUNING.airAccel, dt);
    }

    // Jump, with coyote time and input buffering so it never feels like it ate input.
    if (this._jumpBuffered > 0 && (this.grounded || this._coyote > 0)) {
      if (this.state === STATE.SLIDING) this._endSlide(false);
      this.velocity.y = TUNING.jumpVelocity;
      this.grounded = false;
      this._coyote = 0;
      this._jumpBuffered = 0;
      this.events.emit('audio:play', { id: 'jump', volume: 0.5 });
    }

    this.velocity.y -= TUNING.gravity * dt;
    if (this.velocity.y < -60) this.velocity.y = -60;

    // Mantle check before the sweep: if we're about to hit a waist-high ledge while
    // moving into it and airborne-or-jumping, climb instead of bouncing off.
    if (this.state === STATE.NORMAL && this._tryMantle(move)) return;

    this._delta.copy(this.velocity).multiplyScalar(dt);
    const res = this.physics.sweepCapsule(this.position, TUNING.radius, this.height, this._delta);
    this.position.copy(res.position);

    this.wasGrounded = this.grounded;
    this.grounded = res.grounded;
    if (res.grounded) {
      this._groundMaterial = res.groundMaterial;
      this._coyote = TUNING.coyoteTime;
      if (this.velocity.y < 0) {
        // Landing impact scales with fall speed; hard landings hurt and shake.
        const impact = -this.velocity.y;
        if (!this.wasGrounded && impact > 3) {
          this.events.emit('player:land', { impact });
          this.events.emit('audio:play', {
            id: 'land',
            volume: THREE.MathUtils.clamp(impact / 14, 0.15, 1),
          });
          if (impact > 13) this.damage((impact - 13) * 5.5, null);
        }
        this.velocity.y = 0;
      }
    }
    if (res.ceiling && this.velocity.y > 0) this.velocity.y = 0;
    if (res.hitWall) {
      // Kill velocity along the blocked axes so we don't accumulate into the wall.
      if (res.wallNormal.x !== 0) this.velocity.x = 0;
      if (res.wallNormal.z !== 0) this.velocity.z = 0;
    }

    this._footsteps(dt);
  }

  _targetSpeed(move) {
    if (this.state === STATE.SLIDING) return TUNING.slideSpeed;
    if (!this.grounded) return TUNING.walkSpeed;
    if (this.crouching) return TUNING.crouchSpeed;
    if (this.ads) return TUNING.adsSpeed;
    if (this.tacSprinting) return TUNING.tacSprintSpeed;
    if (this.sprinting) return TUNING.sprintSpeed;
    // Strafing and backpedalling are slower, as in every modern shooter.
    const backwards = move.z > 0.1;
    return TUNING.walkSpeed * (backwards ? 0.78 : 1);
  }

  _applyFriction(dt, coeff) {
    const speed = this.speed;
    if (speed < 0.01) {
      this.velocity.x = 0;
      this.velocity.z = 0;
      return;
    }
    const control = Math.max(speed, TUNING.stopSpeed);
    const drop = control * coeff * dt;
    const scale = Math.max(0, speed - drop) / speed;
    this.velocity.x *= scale;
    this.velocity.z *= scale;
  }

  /** Quake-style accelerate: only adds speed up to the projection limit. */
  _accelerate(wishDir, wishSpeed, accel, dt) {
    const current = this.velocity.x * wishDir.x + this.velocity.z * wishDir.z;
    const add = wishSpeed - current;
    if (add <= 0) return;
    const amount = Math.min(accel * wishSpeed * dt, add);
    this.velocity.x += wishDir.x * amount;
    this.velocity.z += wishDir.z * amount;
  }

  /* --------------------------------- mantle --------------------------------- */

  _tryMantle(move) {
    if (move.z >= -0.5) return false; // must be pushing forward

    // Mantling must be *asked for*, not stumbled into. Anything above step height
    // needs a jump — either already airborne, or a jump buffered this frame. The
    // gate used to be an empty if-block, so walking into chest-high cover yanked
    // you over the top and exposed you; being able to hold position behind cover
    // is a core defensive option and this was silently removing it.
    const asked = !this.grounded || this.velocity.y > 0.1 || this._jumpBuffered > 0;
    if (!asked) return false;

    const origin = _v1.set(this.position.x, this.position.y + 0.55, this.position.z);
    const dir = _v2.copy(this._fwd);
    const wall = this.physics.raycast(origin, dir, TUNING.radius + TUNING.mantleReach, MASK.WORLD | MASK.PROP);
    if (!wall.hit) return false;

    // Probe downward just past the wall to find the ledge top.
    const probe = _v3
      .copy(wall.point)
      .addScaledVector(dir, 0.35)
      .setY(this.position.y + TUNING.mantleMaxHeight + 0.6);
    const down = this.physics.raycast(probe, _down, TUNING.mantleMaxHeight + 0.75, MASK.WORLD | MASK.PROP);
    if (!down.hit) return false;

    const ledgeY = down.point.y;
    const rise = ledgeY - this.position.y;
    if (rise < 0.5 || rise > TUNING.mantleMaxHeight) return false;

    // Headroom on top?
    const top = _v4.set(down.point.x, ledgeY + 0.05, down.point.z);
    const clear = this.physics.sweepCapsule(top, TUNING.radius, TUNING.crouchHeight, _zero);
    if (clear.ceiling) return false;

    this.state = STATE.MANTLING;
    this._mantleTimer = 0;
    this._mantleFrom.copy(this.position);
    this._mantleTo.copy(top);
    this.velocity.set(0, 0, 0);
    this.events.emit('player:mantle', { height: rise });
    this.events.emit('audio:play', { id: 'mantle', volume: 0.7 });
    return true;
  }

  _updateMantle(dt) {
    this._mantleTimer += dt;
    const t = Math.min(1, this._mantleTimer / TUNING.mantleDuration);
    // Vertical first, then forward: the classic "pull up and over" arc.
    const vy = Math.min(1, t / 0.62);
    const hz = Math.max(0, (t - 0.35) / 0.65);
    const ez = hz * hz * (3 - 2 * hz);
    const ey = 1 - Math.pow(1 - vy, 2.2);
    this.position.x = THREE.MathUtils.lerp(this._mantleFrom.x, this._mantleTo.x, ez);
    this.position.z = THREE.MathUtils.lerp(this._mantleFrom.z, this._mantleTo.z, ez);
    this.position.y = THREE.MathUtils.lerp(this._mantleFrom.y, this._mantleTo.y, ey);
    this.crouching = true;
    if (t >= 1) {
      this.state = STATE.NORMAL;
      this.grounded = true;
      this.crouching = false;
    }
  }

  /* --------------------------------- misc ----------------------------------- */

  _footsteps(dt) {
    if (!this.grounded || this.state === STATE.SLIDING) return;
    const s = this.speed;
    if (s < 0.6) {
      this._footDistance = 0;
      return;
    }
    this._footDistance += s * dt;
    const stride = this.crouching ? 1.15 : this.sprinting ? 1.85 : 1.55;
    if (this._footDistance >= stride) {
      this._footDistance -= stride;
      this.events.emit('audio:play', {
        id: `step_${this._groundMaterial}`,
        position: this.position.clone(),
        volume: this.crouching ? 0.25 : this.sprinting ? 0.75 : 0.5,
        pitch: 0.92 + Math.random() * 0.16,
      });
      this.events.emit('player:footstep', { material: this._groundMaterial });
    }
  }

  _updateHealth(dt) {
    if (this.health >= TUNING.maxHealth) return;
    if (this.ctx.engine.elapsed - this.lastDamageTime < TUNING.regenDelay) return;
    this.health = Math.min(TUNING.maxHealth, this.health + TUNING.regenRate * dt);

    // Quantise before broadcasting. Regen used to emit every frame — 93 events for
    // a single 40 HP heal — and each one ran the director's handler and a postfx
    // uniform write. The HUD cannot show a fractional point anyway.
    const shown = Math.ceil(this.health);
    if (shown !== this._shownHealth || this.health >= TUNING.maxHealth) {
      this._shownHealth = shown;
      this.events.emit('player:health', { health: this.health, max: TUNING.maxHealth });
    }
  }

  _updateMatrix() {
    this.matrixWorld.makeRotationY(this.yaw);
    this.matrixWorld.setPosition(this.position.x, this.position.y, this.position.z);
  }

  _broadcastState() {
    const p = this._prevState;
    if (
      p.sprinting !== this.sprinting ||
      p.crouching !== this.crouching ||
      p.ads !== this.ads ||
      p.grounded !== this.grounded ||
      p.sliding !== (this.state === STATE.SLIDING) ||
      p.tac !== this.tacSprinting
    ) {
      p.sprinting = this.sprinting;
      p.crouching = this.crouching;
      p.ads = this.ads;
      p.grounded = this.grounded;
      p.sliding = this.state === STATE.SLIDING;
      p.tac = this.tacSprinting;
      this.events.emit('player:state', {
        sprinting: this.sprinting,
        tacSprinting: this.tacSprinting,
        crouching: this.crouching,
        ads: this.ads,
        grounded: this.grounded,
        sliding: this.state === STATE.SLIDING,
      });
    }
  }

  /**
   * The match has decided we are hit. Write the number, report it, react.
   *
   * Note what is missing: any test for whether this was lethal. `res.fatal` was
   * settled before we were called and `applyDeath` follows on its own. Two places
   * deciding death is how one machine ends a round the other machine is still
   * playing.
   */
  applyDamage(res) {
    this.health = res.after;
    this.lastDamageTime = this.ctx.engine.elapsed;
    const dir = res.dir?.clone?.() ?? null;
    this.events.emit('player:damage', { amount: res.amount, dir, health: this.health });
    this.events.emit('player:health', { health: this.health, max: TUNING.maxHealth });
  }

  applyDeath() {
    if (this.dead) return;
    this.dead = true;
    this.health = 0;
    this.events.emit('player:death', {});
    this.events.emit('game:state', { state: 'dead' });
  }

  /**
   * Convenience for "the world hurt you" — kept because the kill plane and a few
   * scripted effects read better this way than as a full request. It goes through
   * the authority like everything else.
   */
  damage(amount, dir) {
    this.ctx.match?.requestDamage({
      target: this, source: null, amount, dir, means: MEANS.WORLD,
    });
  }

  respawn(pos) {
    this.dead = false;
    this.ctx.match?.revive(this);
    // Opens the window in which the host's (necessarily stale) "you are dead"
    // snapshots are ignored. See `NetSession._applyLocalAuthority`.
    this.ctx.net?.noteLocalRespawn?.();
    this.health = TUNING.maxHealth;
    this.position.copy(pos);
    this.velocity.set(0, 0, 0);
    this.state = STATE.NORMAL;
    this.events.emit('player:health', { health: this.health, max: TUNING.maxHealth });
  }
}

const _zero = new THREE.Vector3(0, 0, 0);
const _down = new THREE.Vector3(0, -1, 0);
const _moveTmp = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
